/**
 * The EMT's own view of their readiness (feature flag `emt_readiness_v2`). Pure: no Firebase, no React.
 *
 * It READS the existing breakdown from calculateReadiness() and never changes it. Employers keep the
 * percentages and the weighted math exactly as before; this module only decides what the EMT is
 * shown instead of the big overall number:
 *
 *  - each part (Credentials, Documentation, Experience, Skills, Profile) as "Complete" or "Needs work",
 *    with one concrete action and a link (partStatuses);
 *  - a profile competitiveness grade, Building / Competitive / Strong (competitivenessGrade);
 *  - optional, dismissible suggestions for standing out (readinessSuggestions);
 *  - the single next best step (nextBestStep);
 *  - whether to celebrate a newly complete profile (celebrationAction);
 *  - whether the EMT meets one job's requirements (jobFitStatus).
 *
 * The breakdown's `details` lines are the source of truth for what is missing: calculateReadiness
 * writes "✓ ..." for something on file, "✗ ..." for something missing or expired, and "⚠️ ..." for a
 * cap it applied (an expired core credential or required document).
 */
import type { ReadinessBreakdown } from '../../types/readiness';

/** Feature flag for everything in this module's UI. Off (the default) = today's EMT view, exactly. */
export const EMT_READINESS_V2_FLAG = 'emt_readiness_v2';
export const EMT_READINESS_V2_FLAG_LABEL = 'EMT readiness view (v2)';
export const EMT_READINESS_V2_FLAG_DESCRIPTION =
  'EMTs see each part of their readiness as Complete or Needs work instead of one overall number, a Building / Competitive / Strong grade, a next-step helper, a per-job thumbs-up or what is missing, and far-off jobs hidden behind "Show all jobs". Employers keep their percentages.';

export type ReadinessPartKey = 'certifications' | 'documentation' | 'experience' | 'skills' | 'profileActivity';
export type ReadinessPartStatus = 'complete' | 'needs_work';
export type CompetitivenessGrade = 'building' | 'competitive' | 'strong';

/** One concrete thing to do, with where to do it. `trainingCert` adds a "find training" link. */
export type ReadinessAction = {
  id: string;
  label: string;
  description: string;
  href: string;
  trainingCert?: string;
};

export type ReadinessPartView = {
  key: ReadinessPartKey;
  label: string;
  status: ReadinessPartStatus;
  /** One plain sentence: what is on file, or what is missing. */
  summary: string;
  /** Everything still missing in this part, in plain words (empty when complete). */
  issues: string[];
  /** The next thing to do for this part (null when complete). */
  action: ReadinessAction | null;
};

/** Order = the weight calculateReadiness gives each part, so it is also the order of impact. */
export const READINESS_PART_ORDER: ReadinessPartKey[] = ['certifications', 'documentation', 'experience', 'skills', 'profileActivity'];

export const READINESS_PART_LABELS: Record<ReadinessPartKey, string> = {
  certifications: 'Credentials',
  documentation: 'Documentation',
  experience: 'Experience',
  skills: 'Skills',
  profileActivity: 'Profile',
};

export const GRADE_LABELS: Record<CompetitivenessGrade, string> = {
  building: 'Building',
  competitive: 'Competitive',
  strong: 'Strong',
};

/** Skills count that makes the Skills part Complete (calculateReadiness gives 60 of 100 from 5). */
export const SKILLS_COMPLETE_COUNT = 5;

export const ACCOUNT_CERTIFICATIONS_HREF = '/account#certifications';
export const ACCOUNT_DOCUMENTS_HREF = '/account#profile-documents';
export const ACCOUNT_EXPERIENCE_HREF = '/account#past-experience';
export const ACCOUNT_SKILLS_HREF = '/account#skills';
export const ACCOUNT_BIO_HREF = '/account#bio';
export const ACCOUNT_HREF = '/account';

/** Same deep link the job page's "Strengthen your match" uses (buildStrengthenAccountPath), kept pure here. */
function strengthenHref(cert: string): string {
  const params = new URLSearchParams();
  params.set('strengthen', cert);
  return `/account?${params.toString()}#certifications`;
}

const isMissing = (d: string) => d.startsWith('✗');
const isCap = (d: string) => d.startsWith('⚠️');
const strip = (d: string) => d.replace(/^(✗|✓|⚠️)\s*/u, '').trim();

/* ------------------------------------------------------------------ Credentials */

/**
 * Credentials are Complete when NREMT (or the matching EMT level, or a state certification standing in
 * for it where the state does not require NREMT), the state license and BLS all COUNT (document reviewed
 * and current, the counting rule) and nothing core has expired: no "✗" line other than the optional ACLS
 * one, and no "⚠️ Core credential expired" cap. ACLS / PALS / PHTLS are extras (see readinessSuggestions).
 */
function credentialsPart(details: string[]): ReadinessPartView {
  const missing = details.filter((d) => isMissing(d) && !/^✗ ACLS\b/.test(d));
  const capped = details.some(isCap);
  const status: ReadinessPartStatus = missing.length === 0 && !capped ? 'complete' : 'needs_work';
  const issues = missing.map((d) => {
    const t = strip(d);
    if (/^(NREMT|EMT certification) expired/i.test(t)) return 'Your EMT certification has expired.';
    if (/^(NREMT|EMT certification) document not reviewed/i.test(t)) return 'Your NREMT or EMT certification is on your profile, but its document has not passed review yet, so it does not count yet.';
    if (/^(NREMT|EMT certification) not on file/i.test(t)) return 'No NREMT or EMT certification on your profile yet.';
    if (/^State license expired/i.test(t)) return 'Your state license has expired.';
    if (/^State license document not reviewed/i.test(t)) return 'Your state license is on your profile, but its document has not passed review yet, so it does not count yet.';
    if (/^State license not on file/i.test(t)) return 'No state license on your profile yet.';
    if (/^BLS expired/i.test(t)) return 'Your BLS / CPR card has expired.';
    if (/^BLS document not reviewed/i.test(t)) return 'Your BLS / CPR card is on your profile, but its document has not passed review yet, so it does not count yet.';
    if (/^BLS not active/i.test(t)) return 'No active BLS / CPR card on your profile.';
    return `${t}.`;
  });
  let action: ReadinessAction | null = null;
  const first = missing[0] ? strip(missing[0]) : '';
  if (status === 'needs_work') {
    if (/^(NREMT|EMT certification) expired/i.test(first)) {
      action = { id: 'cred-renew-nremt', label: 'Renew your EMT certification', description: 'Add your renewed certification and its new expiration date.', href: ACCOUNT_CERTIFICATIONS_HREF, trainingCert: 'NREMT-B' };
    } else if (/document not reviewed/i.test(first)) {
      action = { id: 'cred-upload-document', label: 'Upload the document', description: 'A clear photo or PDF of the card. It counts once it passes our document review.', href: ACCOUNT_CERTIFICATIONS_HREF };
    } else if (/^BLS expired/i.test(first)) {
      action = { id: 'cred-renew-bls', label: 'Renew your BLS / CPR card', description: 'Add your renewed card and its new expiration date.', href: ACCOUNT_CERTIFICATIONS_HREF, trainingCert: 'BLS' };
    } else if (/^(NREMT|EMT certification) not on file/i.test(first)) {
      action = { id: 'cred-add-level', label: 'Add your EMT certification', description: 'Add your NREMT or EMT level (EMT-B, AEMT or Paramedic) with its expiration date.', href: strengthenHref('EMT-B'), trainingCert: 'EMT-B' };
    } else if (/^State license expired/i.test(first)) {
      action = { id: 'cred-renew-state', label: 'Renew your state license', description: 'Add your renewed state license and its new expiration date.', href: ACCOUNT_CERTIFICATIONS_HREF };
    } else if (/^State license not on file/i.test(first)) {
      action = { id: 'cred-add-state', label: 'Add your state license', description: 'Add the EMS license or certification your state issued you.', href: strengthenHref('State EMS Certification') };
    } else if (/^BLS not active/i.test(first)) {
      action = { id: 'cred-add-bls', label: 'Add your BLS / CPR card', description: 'Add your current BLS card with its expiration date.', href: strengthenHref('BLS'), trainingCert: 'BLS' };
    } else {
      action = { id: 'cred-review', label: 'Review your credentials', description: 'Update anything expired so it counts again.', href: ACCOUNT_CERTIFICATIONS_HREF };
    }
  }
  return {
    key: 'certifications',
    label: READINESS_PART_LABELS.certifications,
    status,
    summary: status === 'complete' ? 'Your core credentials are on file and current.' : issues[0] || 'A core credential has expired.',
    issues: issues.length ? issues : capped ? ['A core credential has expired.'] : [],
    action,
  };
}

/* ------------------------------------------------------------------ Documentation */

const DOC_UPLOAD_NAMES: Record<string, string> = {
  // 'NREMT card' is the label readiness lines saved before 2026-10-28 still carry.
  'NREMT card': 'EMT certification',
  'EMT certification (NREMT or state)': 'EMT certification',
  'State license': 'state license',
  'BLS card': 'BLS card',
  "Driver's license": "driver's license",
};

/**
 * Documentation is Complete when every required document (NREMT card, state license, BLS card,
 * driver's license) is on file and none is expired: no "✗" line and no "⚠️ Required document
 * expired" cap. A credential document only counts once it has passed review, so "missing" here can
 * also mean "uploaded, still being reviewed" and the copy says so.
 */
function documentationPart(details: string[]): ReadinessPartView {
  const missing = details.filter(isMissing);
  const capped = details.some(isCap);
  const status: ReadinessPartStatus = missing.length === 0 && !capped ? 'complete' : 'needs_work';
  const parsed = missing.map((d) => {
    const t = strip(d);
    const expired = / expired$/i.test(t);
    const label = t.replace(/ (missing|expired)$/i, '');
    return { label, expired };
  });
  const issues = parsed.map((p) =>
    p.expired ? `Your ${DOC_UPLOAD_NAMES[p.label] ?? p.label} has expired.` : `${p.label}: not on file yet, or still being reviewed.`,
  );
  const first = parsed[0];
  const action: ReadinessAction | null =
    status === 'complete'
      ? null
      : first
        ? {
            id: `doc-${first.expired ? 'renew' : 'upload'}-${(first.label === 'EMT certification (NREMT or state)' ? 'NREMT card' : first.label).toLowerCase().replace(/[^a-z]+/g, '-')}`,
            label: `${first.expired ? 'Upload your current' : 'Upload your'} ${DOC_UPLOAD_NAMES[first.label] ?? first.label}`,
            description: 'A clear photo or PDF of the card. It counts once it passes our document review.',
            href: ACCOUNT_DOCUMENTS_HREF,
          }
        : { id: 'doc-review', label: 'Review your documents', description: 'Replace the expired document so it counts again.', href: ACCOUNT_DOCUMENTS_HREF };
  return {
    key: 'documentation',
    label: READINESS_PART_LABELS.documentation,
    status,
    summary: status === 'complete' ? 'Your required documents are on file and reviewed.' : issues[0] || 'A required document has expired.',
    issues: issues.length ? issues : capped ? ['A required document has expired.'] : [],
    action,
  };
}

/* ------------------------------------------------------------------ Experience */

/**
 * Experience is Complete when the profile states any EMS experience: at least one year, or at least
 * one past role. calculateReadiness scores exactly 0 when neither is on file, so the test is score > 0.
 * This is about the profile being complete, not seniority: a newer EMT is not told to "work" on years.
 */
function experiencePart(score: number, details: string[]): ReadinessPartView {
  const status: ReadinessPartStatus = score > 0 ? 'complete' : 'needs_work';
  const years = details[0] || '';
  return {
    key: 'experience',
    label: READINESS_PART_LABELS.experience,
    status,
    summary: status === 'complete' ? `${years} on your profile.` : 'No EMS experience on your profile yet.',
    issues: status === 'complete' ? [] : ['Add your years of EMS experience or a past role.'],
    action:
      status === 'complete'
        ? null
        : { id: 'exp-add', label: 'Add your EMS experience', description: 'Your years of experience and the EMS roles you have held.', href: ACCOUNT_EXPERIENCE_HREF },
  };
}

/* ------------------------------------------------------------------ Skills */

export function skillCountFromDetails(details: string[]): number {
  const m = /^(\d+) skill/.exec(details[0] || '');
  return m ? Number(m[1]) : 0;
}

/** Skills are Complete from SKILLS_COMPLETE_COUNT (5) selected skills (score 60 or more). */
function skillsPart(score: number, details: string[]): ReadinessPartView {
  const count = skillCountFromDetails(details);
  const status: ReadinessPartStatus = score >= 60 || count >= SKILLS_COMPLETE_COUNT ? 'complete' : 'needs_work';
  const need = Math.max(1, SKILLS_COMPLETE_COUNT - count);
  return {
    key: 'skills',
    label: READINESS_PART_LABELS.skills,
    status,
    summary: status === 'complete' ? `${count} skills on your profile.` : `${count} of ${SKILLS_COMPLETE_COUNT} skills added.`,
    issues: status === 'complete' ? [] : [`Add ${need} more skill${need === 1 ? '' : 's'}.`],
    action:
      status === 'complete'
        ? null
        : { id: 'skills-add', label: `Add ${need} more skill${need === 1 ? '' : 's'}`, description: 'Pick the EMS skills you use, like airway, cardiac or trauma care.', href: ACCOUNT_SKILLS_HREF },
  };
}

/* ------------------------------------------------------------------ Profile */

/**
 * Profile is Complete with a profile photo and a bio. The breakdown's other two Profile lines are not
 * part of "complete": "active in the last 30 days" is not something on the profile, and keeping the
 * profile public is the EMT's privacy choice (it is offered as a dismissible suggestion instead).
 */
function profilePart(details: string[]): ReadinessPartView {
  const noPhoto = details.some((d) => /^✗ Add a profile photo/.test(d));
  const noBio = details.some((d) => /^✗ Add a bio/.test(d));
  const status: ReadinessPartStatus = !noPhoto && !noBio ? 'complete' : 'needs_work';
  const issues = [noPhoto ? 'No profile photo yet.' : '', noBio ? 'No bio yet.' : ''].filter(Boolean);
  const action: ReadinessAction | null = noPhoto
    ? { id: 'profile-photo', label: 'Add a profile photo', description: 'A clear, friendly headshot helps employers remember you.', href: ACCOUNT_HREF }
    : noBio
      ? { id: 'profile-bio', label: 'Write a short bio', description: 'Two or three sentences: your level, your experience and what you are looking for.', href: ACCOUNT_BIO_HREF }
      : null;
  return {
    key: 'profileActivity',
    label: READINESS_PART_LABELS.profileActivity,
    status,
    summary: status === 'complete' ? 'Photo and bio are on your profile.' : issues.join(' '),
    issues,
    action,
  };
}

/* ------------------------------------------------------------------ Driver / non-certified */

/**
 * What the view needs about a person who chose "Driver / non-certified" (flag driver_roles). The
 * caller reads it from the profile (src/lib/jobs/driverRoles.ts profileHasDriverItem), so this module
 * stays free of profile shapes. A driver's view is about driving: no NREMT, state EMS license or EMS
 * skills are asked for, and the employer's percentages are unchanged.
 */
export type DriverReadinessInput = {
  /** A Dispatcher: same view without the driving suggestions (EVOC, driving record). */
  dispatcher?: boolean;
  /** A driver's license document that passed the document review. */
  hasDriversLicense: boolean;
  /** A driving record (MVR) document on file. */
  hasDrivingRecord: boolean;
  /** A CPR / BLS card on file (document reviewed). */
  hasCprBls: boolean;
  /** EVOC on the profile (any status). */
  hasEvoc: boolean;
};

/** Driver Credentials: Complete with a reviewed driver's license on file. */
function driverCredentialsPart(d: DriverReadinessInput): ReadinessPartView {
  const status: ReadinessPartStatus = d.hasDriversLicense ? 'complete' : 'needs_work';
  const issue = "No driver's license on your profile yet, or it is still being reviewed.";
  return {
    key: 'certifications',
    label: READINESS_PART_LABELS.certifications,
    status,
    summary: status === 'complete' ? "Your driver's license is on file and reviewed." : issue,
    issues: status === 'complete' ? [] : [issue],
    action:
      status === 'complete'
        ? null
        : { id: 'driver-upload-license', label: "Upload your driver's license", description: 'A clear photo or PDF of the front of your license. It counts once it passes our document review.', href: ACCOUNT_DOCUMENTS_HREF },
  };
}

/**
 * Driver Documentation: Complete with a reviewed driver's license (2026-10-10, B2). The driving record
 * (MVR) is arranged by the employer, so it is never required here; adding one is an optional suggestion.
 */
function driverDocumentationPart(d: DriverReadinessInput): ReadinessPartView {
  const status: ReadinessPartStatus = d.hasDriversLicense ? 'complete' : 'needs_work';
  const issue = "Your driver's license is not on file yet, or it is still being reviewed.";
  return {
    key: 'documentation',
    label: READINESS_PART_LABELS.documentation,
    status,
    summary: status === 'complete' ? "Your driver's license is reviewed. Employers check your driving record themselves." : issue,
    issues: status === 'complete' ? [] : [issue],
    action:
      status === 'complete'
        ? null
        : { id: 'driver-upload-license', label: "Upload your driver's license", description: 'A clear photo or PDF of the front of your license. It counts once it passes our document review.', href: ACCOUNT_DOCUMENTS_HREF },
  };
}

/** Experience for a driver: same rule (any experience on file), plain wording without "EMS". */
function driverExperiencePart(score: number, details: string[]): ReadinessPartView {
  const part = experiencePart(score, details);
  if (part.status === 'complete') return part;
  return {
    ...part,
    summary: 'No work experience on your profile yet.',
    issues: ['Add your years of experience or a past job.'],
    action: { id: 'exp-add', label: 'Add your experience', description: 'Your years of experience and the jobs you have held, driving included.', href: ACCOUNT_EXPERIENCE_HREF },
  };
}

export type PartStatusOptions = {
  /** Set only for a person who chose "Driver / non-certified" while the driver_roles flag is on. */
  driver?: DriverReadinessInput | null;
};

/** Every part, in impact order, as Complete / Needs work with its next action. */
export function partStatuses(breakdown: ReadinessBreakdown, opts: PartStatusOptions = {}): ReadinessPartView[] {
  const p = breakdown.pillars;
  if (opts.driver) {
    // A driver's parts: driving credentials, driving record, experience, profile. EMS skills are not a part.
    return [
      driverCredentialsPart(opts.driver),
      driverDocumentationPart(opts.driver),
      driverExperiencePart(p.experience.score, p.experience.details),
      profilePart(p.profileActivity.details),
    ];
  }
  return [
    credentialsPart(p.certifications.details),
    documentationPart(p.documentation.details),
    experiencePart(p.experience.score, p.experience.details),
    skillsPart(p.skills.score, p.skills.details),
    profilePart(p.profileActivity.details),
  ];
}

export function allPartsComplete(parts: ReadinessPartView[]): boolean {
  return parts.length > 0 && parts.every((x) => x.status === 'complete');
}

/* ------------------------------------------------------------------ Grade */

export type GradeView = {
  grade: CompetitivenessGrade;
  label: string;
  completeCount: number;
  totalParts: number;
  /** Plain words: what this grade means. */
  meaning: string;
  /** Plain words: how to reach the next grade (null at Strong). */
  howToMoveUp: string | null;
  /** Parts still to complete for the next grade. */
  nextGradeParts: string[];
};

/**
 * THE grade rule (documented in the slice report too):
 *  - Strong:      every part is Complete.
 *  - Competitive: Credentials AND Documentation are Complete, plus at least one more part.
 *                 Those two are what employers check first and what the apply gate reads.
 *  - Building:    anything else.
 */
export function competitivenessGrade(parts: ReadinessPartView[]): GradeView {
  const done = (k: ReadinessPartKey) => parts.find((x) => x.key === k)?.status === 'complete';
  const completeCount = parts.filter((x) => x.status === 'complete').length;
  const coreDone = done('certifications') && done('documentation');
  const othersDone = (['experience', 'skills', 'profileActivity'] as ReadinessPartKey[]).filter(done).length;
  const totalParts = parts.length;
  if (totalParts > 0 && completeCount === totalParts) {
    return {
      grade: 'strong',
      label: GRADE_LABELS.strong,
      completeCount,
      totalParts,
      meaning: 'Every part of your profile is complete. Employers see a full picture of you.',
      howToMoveUp: null,
      nextGradeParts: [],
    };
  }
  if (coreDone && othersDone >= 1) {
    const remaining = parts.filter((x) => x.status !== 'complete').map((x) => x.label);
    return {
      grade: 'competitive',
      label: GRADE_LABELS.competitive,
      completeCount,
      totalParts,
      meaning: 'Your credentials and documents are in order, so you can be considered for jobs at your level.',
      howToMoveUp: `To reach Strong, complete ${joinWords(remaining)}.`,
      nextGradeParts: remaining,
    };
  }
  const needed: string[] = [];
  if (!done('certifications')) needed.push(READINESS_PART_LABELS.certifications);
  if (!done('documentation')) needed.push(READINESS_PART_LABELS.documentation);
  const needOneMore = othersDone === 0;
  // "Experience, Skills or Profile" for an EMT; a driver's view has no Skills part.
  const otherLabels = (['experience', 'skills', 'profileActivity'] as ReadinessPartKey[])
    .filter((k) => parts.some((x) => x.key === k))
    .map((k) => READINESS_PART_LABELS[k]);
  const othersText = otherLabels.length <= 1 ? otherLabels.join('') : `${otherLabels.slice(0, -1).join(', ')} or ${otherLabels[otherLabels.length - 1]}`;
  const parts2 = needOneMore ? [...needed, `one more part (${othersText})`] : needed;
  return {
    grade: 'building',
    label: GRADE_LABELS.building,
    completeCount,
    totalParts,
    meaning: 'You are still building your profile. Employers look at credentials and documents first.',
    howToMoveUp: `To reach Competitive, complete ${joinWords(parts2)}.`,
    nextGradeParts: needOneMore ? [...needed, othersText] : needed,
  };
}

function joinWords(list: string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/* ------------------------------------------------------------------ Suggestions */

export type ReadinessSuggestion = ReadinessAction & { dismissible: true };

/**
 * Optional ways to stand out beyond "Complete". Each has a stable id so a dismissal (stored in the
 * profile at preferences.readinessDismissed) keeps it hidden. Dismissed ids are filtered out here.
 */
export function readinessSuggestions(
  breakdown: ReadinessBreakdown,
  opts: { dismissed?: readonly string[]; publicProfile?: boolean; heldCertNames?: readonly string[]; driver?: DriverReadinessInput | null } = {},
): ReadinessSuggestion[] {
  const dismissed = new Set(opts.dismissed ?? []);
  if (opts.driver) {
    // A driver is never pointed at EMS certifications (ACLS, PALS, PHTLS) or EMS skills.
    const d = opts.driver;
    const forDriver: ReadinessSuggestion[] = [];
    if (!d.dispatcher && !d.hasEvoc) forDriver.push({ id: 'suggest-evoc', label: 'Take an EVOC course', description: 'An emergency vehicle operator course is often required to drive an ambulance.', href: strengthenHref('EVOC'), trainingCert: 'EVOC', dismissible: true });
    if (!d.hasCprBls) forDriver.push({ id: 'suggest-driver-cpr', label: 'Add a CPR / BLS card', description: 'Many transport employers ask drivers for a current CPR or BLS card.', href: strengthenHref('BLS'), trainingCert: 'BLS', dismissible: true });
    if (!d.dispatcher && !d.hasDrivingRecord) forDriver.push({ id: 'suggest-driver-mvr', label: 'Add your driving record (optional)', description: 'Employers check your driving record (MVR) themselves. Adding yours can speed that up. Your choice.', href: ACCOUNT_DOCUMENTS_HREF, dismissible: true });
    if (opts.publicProfile === false) {
      forDriver.push({ id: 'suggest-public-profile', label: 'Let employers find you', description: 'Your profile is private. Turning on your public profile lets employers find you. Your choice.', href: ACCOUNT_HREF, dismissible: true });
    }
    return forDriver.filter((s) => !dismissed.has(s.id));
  }
  const certDetails = breakdown.pillars.certifications.details;
  const held = (opts.heldCertNames ?? []).map((n) => String(n).toLowerCase());
  const has = (re: RegExp) => certDetails.some((d) => d.startsWith('✓') && re.test(d));
  const holdsEvoc = held.some((n) => /\bevoc\b|emergency vehicle/.test(n));
  const out: ReadinessSuggestion[] = [];
  if (!has(/\bACLS\b/)) {
    out.push({ id: 'suggest-acls', label: 'Add ACLS', description: 'Advanced cardiac life support is asked for by many 911 and critical care employers.', href: strengthenHref('ACLS'), trainingCert: 'ACLS', dismissible: true });
  }
  if (!has(/\bPALS\b/)) {
    out.push({ id: 'suggest-pals', label: 'Add PALS', description: 'Pediatric advanced life support helps for jobs with pediatric calls.', href: strengthenHref('PALS'), trainingCert: 'PALS', dismissible: true });
  }
  if (!has(/\bPHTLS\b/)) {
    out.push({ id: 'suggest-phtls', label: 'Add PHTLS', description: 'Prehospital trauma life support shows trauma training.', href: ACCOUNT_CERTIFICATIONS_HREF, dismissible: true });
  }
  if (!holdsEvoc) out.push({ id: 'suggest-evoc', label: 'Take an EVOC course', description: 'An emergency vehicle operator course is often required to drive for an agency.', href: strengthenHref('EVOC'), trainingCert: 'EVOC', dismissible: true });
  const skills = skillCountFromDetails(breakdown.pillars.skills.details);
  if (skills >= SKILLS_COMPLETE_COUNT && skills < 10) {
    out.push({ id: 'suggest-skills-10', label: 'Add more skills', description: 'Ten or more skills across different areas makes your profile stand out.', href: ACCOUNT_SKILLS_HREF, dismissible: true });
  }
  const variety = breakdown.pillars.experience.details.find((d) => d.startsWith('Role variety:'));
  const roleCount = variety ? variety.replace('Role variety:', '').split(',').filter((s) => s.trim()).length : 0;
  if (breakdown.pillars.experience.score > 0 && roleCount < 3) {
    out.push({ id: 'suggest-role-variety', label: 'Add your past roles', description: 'Listing the kinds of EMS work you have done (911, interfacility, events) shows range.', href: ACCOUNT_EXPERIENCE_HREF, dismissible: true });
  }
  if (opts.publicProfile === false) {
    out.push({ id: 'suggest-public-profile', label: 'Let employers find you', description: 'Your profile is private. Turning on your public profile lets employers find you. Your choice.', href: ACCOUNT_HREF, dismissible: true });
  }
  return out.filter((s) => !dismissed.has(s.id));
}

/* ------------------------------------------------------------------ Next best step */

export type NextBestStep = ReadinessAction & {
  /** Why this one first, in plain words. */
  reason: string;
  kind: 'renew' | 'part' | 'expiring' | 'suggestion';
};

/**
 * The single highest-impact action, in this order:
 *  1. something expired (it caps the Credentials or Documentation part until replaced);
 *  2. the first part that Needs work, in weight order (Credentials 35, Documentation 25,
 *     Experience 20, Skills 15, Profile 5), using that part's own action;
 *  3. a document expiring within 30 days (soonest first);
 *  4. the first suggestion not dismissed.
 * Null when there is nothing left to do.
 */
export function nextBestStep(
  breakdown: ReadinessBreakdown,
  parts: ReadinessPartView[] = partStatuses(breakdown),
  suggestions: ReadinessSuggestion[] = [],
): NextBestStep | null {
  const expired = breakdown.expiredDocs[0];
  if (expired) {
    return {
      id: `renew-${expired.type}`,
      label: `Renew your ${expired.type}`,
      description: 'It has expired, so it no longer counts. Add the renewed card and its new date.',
      href: ACCOUNT_CERTIFICATIONS_HREF,
      trainingCert: expired.type,
      reason: 'Expired credentials hold your profile back the most.',
      kind: 'renew',
    };
  }
  const part = parts.find((x) => x.status === 'needs_work' && x.action);
  if (part && part.action) {
    return { ...part.action, reason: `${part.label} is the most important part you have left.`, kind: 'part' };
  }
  const expiring = [...breakdown.expiringDocs].sort((a, b) => a.daysUntil - b.daysUntil)[0];
  if (expiring) {
    return {
      id: `expiring-${expiring.type}`,
      label: `Renew your ${expiring.type}`,
      description: `It expires in ${expiring.daysUntil} day${expiring.daysUntil === 1 ? '' : 's'}. Renew it before it lapses.`,
      href: ACCOUNT_CERTIFICATIONS_HREF,
      trainingCert: expiring.type,
      reason: 'Keep your credentials current so you stay eligible.',
      kind: 'expiring',
    };
  }
  const s = suggestions[0];
  if (s) {
    return { id: s.id, label: s.label, description: s.description, href: s.href, trainingCert: s.trainingCert, reason: 'Your profile is complete. This is an optional way to stand out.', kind: 'suggestion' };
  }
  return null;
}

/* ------------------------------------------------------------------ Celebration */

export type CelebrationAction = 'celebrate' | 'none' | 'reset';

/**
 * Confetti once per completion. `seen` is true when this completion was already celebrated (the
 * browser's remembered flag OR the profile's preferences.readinessCelebrated). When the profile is no
 * longer complete the flag is reset, so the NEXT completion is celebrated again.
 */
export function celebrationAction(allComplete: boolean, seen: boolean): CelebrationAction {
  if (allComplete) return seen ? 'none' : 'celebrate';
  return seen ? 'reset' : 'none';
}

/* ------------------------------------------------------------------ Per-job fit */

export type JobFitInput = {
  canApply: boolean;
  applyAnyway: boolean;
  missingOnProfile: string[];
  pendingReview: Array<{ certification: string; reason: string }>;
};

export type JobFit = {
  /** True when nothing is missing for this job: no hard gate and no document gap. */
  meets: boolean;
  missing: string[];
  pending: Array<{ certification: string; reason: string }>;
  /** The employer accepts applications without the missing credential (the EMT may still apply). */
  canApplyAnyway: boolean;
};

/** Thumbs-up exactly when evaluation.canApply holds WITHOUT the employer's "apply anyway" switch. */
export function jobFitStatus(e: JobFitInput): JobFit {
  return {
    meets: e.canApply && !e.applyAnyway,
    missing: e.missingOnProfile,
    pending: e.pendingReview,
    canApplyAnyway: e.applyAnyway,
  };
}
